package co.maytes.standupbot.dashboard.features.roster

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.SkeletonList
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.TextInput
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.integration.LinearUserDto
import co.maytes.standupbot.shared.roster.LinearUserRanker
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.RadioInput
import org.jetbrains.compose.web.dom.Text

private const val LIST_MAX_HEIGHT_PX = 260
private var selectCounter = 0

@Composable
fun LinearUserSelect(
    state: LinearUsersState,
    hintName: String?,
    hintEmail: String?,
    selectedId: String?,
    onSelect: (LinearUserDto) -> Unit,
    onRetry: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = Modifier.fillMaxWidth().then(modifier).gap(Spacing.sm)) {
        when (state) {
            LinearUsersState.Idle, LinearUsersState.Loading -> SkeletonList(rows = 4)
            LinearUsersState.NotConnected ->
                SelectNotice {
                    Text("Linear isn't connected for this workspace. ")
                    Link(path = "/app/settings/integrations", text = "Connect Linear")
                }
            is LinearUsersState.Failed ->
                Column(modifier = Modifier.gap(Spacing.sm)) {
                    SelectNotice { Text("Couldn't load the people in Linear: ${state.message}") }
                    Row { ActionButton(label = "Retry", onClick = onRetry) }
                }
            is LinearUsersState.Ready ->
                Options(
                    users = state.users,
                    hintName = hintName,
                    hintEmail = hintEmail,
                    selectedId = selectedId,
                    onSelect = onSelect,
                )
        }
    }
}

@Composable
private fun Options(
    users: List<LinearUserDto>,
    hintName: String?,
    hintEmail: String?,
    selectedId: String?,
    onSelect: (LinearUserDto) -> Unit,
) {
    var query by remember { mutableStateOf("") }
    val groupName = remember { "linear-user-${selectCounter++}" }
    val ranked = remember(users, hintName, hintEmail, query) { LinearUserRanker.rank(users, hintName, hintEmail, query) }
    val hasHint = !hintName.isNullOrBlank() || !hintEmail.isNullOrBlank()
    val bestId =
        ranked.firstOrNull()?.takeIf { hasHint && it.active && LinearUserRanker.closeness(it, hintName, hintEmail) > 0 }?.id

    TextInput(
        value = query,
        onValueChange = { query = it },
        placeholder = "Search Linear people by name or email…",
        ariaLabel = "Search Linear people",
    )
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .role("radiogroup")
            .ariaLabel("Linear people")
            .padding(Spacing.xxs)
            .styleModifier {
                property("max-height", "${LIST_MAX_HEIGHT_PX}px")
                property("overflow-y", "auto")
                property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
                property("border-radius", "${Radius.md}")
            },
    ) {
        if (ranked.isEmpty()) {
            SpanText(
                text = if (users.isEmpty()) "Linear has no people to pick from." else "No Linear person matches \"${query.trim()}\".",
                modifier = Modifier.padding(Spacing.sm).fontSize(Typography.bodySmall.size).color(Palette.textSecondary),
            )
        }
        ranked.forEach { user ->
            UserOption(
                user = user,
                groupName = groupName,
                selected = user.id == selectedId,
                bestMatch = user.id == bestId,
                onSelect = { onSelect(user) },
            )
        }
    }
}

@Composable
private fun UserOption(
    user: LinearUserDto,
    groupName: String,
    selected: Boolean,
    bestMatch: Boolean,
    onSelect: () -> Unit,
) {
    Label(
        attrs = RosterRowStyle.toModifier()
            .asCheckRow()
            .fillMaxWidth()
            .padding(topBottom = Spacing.xs, leftRight = Spacing.sm)
            .borderRadius(Radius.sm)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "${Spacing.sm}")
                property("box-sizing", "border-box")
                if (selected) property("background-color", Palette.accentSubtle.cssRgb())
                if (!user.active) property("opacity", "0.7")
            }
            .toAttrs(),
    ) {
        RowRadioControl(selected = selected, groupName = groupName, onSelect = onSelect)
        Column(
            modifier = Modifier.styleModifier {
                property("flex", "1 1 auto")
                property("min-width", "0")
            },
        ) {
            SpanText(
                text = user.name,
                modifier = Modifier.fontSize(Typography.body.size).fontWeight(500).color(Palette.textPrimary),
            )
            user.email?.let {
                SpanText(
                    text = it,
                    modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textTertiary),
                )
            }
        }
        if (bestMatch) Badge(label = "closest match", tone = BadgeTone.Accent)
        if (!user.active) Badge(label = "deactivated", tone = BadgeTone.Neutral)
    }
}

@Composable
private fun SelectNotice(content: @Composable () -> Unit) {
    Div(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = Spacing.sm, leftRight = Spacing.md)
            .borderRadius(Radius.md)
            .fontSize(Typography.bodySmall.size)
            .color(Palette.textPrimary)
            .styleModifier {
                property("background", Palette.accentSubtle.cssRgb())
                property("border", "1px solid ${Palette.border.cssRgb()}")
                property("box-sizing", "border-box")
            }
            .toAttrs { attr("role", "note") },
    ) {
        content()
    }
}
